import {
    useEffect,
    useMemo,
    useRef,
    useState,
    useCallback,
    type JSX,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Search,
    Filter,
    Plus,
    Calendar,
    CheckSquare,
    CheckCircle2,
    AlertTriangle,
    ChevronLeft,
    ChevronRight,
    ChevronUp,
    ChevronDown,
    X,
    LayoutGrid,
    List,
    ArrowUpDown,
    Pencil,
    MessageSquare,
    Paperclip,
    Folder,
    Archive,
    UserCheck,
    Clock,
    GripVertical,
    CalendarRange,
} from "lucide-react";
import {
    DndContext,
    DragOverlay,
    KeyboardSensor,
    PointerSensor,
    useDroppable,
    useDraggable,
    useSensor,
    useSensors,
    type DraggableAttributes,
    type DragEndEvent,
    type DragStartEvent,
} from "@dnd-kit/core";
import api from "../api/client";
import { useAuth } from "../hooks/useAuth";
import { useProject } from "../hooks/useProject";
import { hasPermission } from "../lib/permissions";
import { toLocalDateKeyTR } from "../lib/date";
import { useRealtimeTasks } from "../hooks/useRealtime";
import { ListSkeleton } from "../components/common/ListSkeleton";
import { EmptyState } from "../components/common/EmptyState";
import type { Task, User as UserType, Project, UserGroup } from "../types";
import TaskModal from "../components/tasks/TaskModal";
import LookaheadContent from "./LookaheadContent";

type KanbanStatus =
    | "atandi"
    | "yapiliyor"
    | "beklemede"
    | "pending_approval"
    | "tamamlandi"
    | "iptal";
type ViewMode =
    | "kanban"
    | "list"
    | "calendar"
    | "lookahead"
    | "completed"
    | "archived"
    | "assigned-to-me"
    | "pending_approval";
type SortField =
    | "created_at"
    | "due_date"
    | "priority"
    | "title"
    | "status"
    | "completed_at";

// Kanban/takvim görünümünde sayfalama UI'ı yoktur; backend per_page'i max 100
// ile sınırladığı için 100'lük sayfalar birleştirilerek bu tavana kadar çekilir.
const BOARD_PAGE_SIZE = 100;
const BOARD_MAX_TASKS = 1000;

const KANBAN_COLUMNS: {
    key: KanbanStatus;
    label: string;
    color: string;
    bg: string;
    border: string;
    apiStatus: string;
    readOnly?: boolean;
}[] = [
    {
        key: "atandi",
        label: "Atandı",
        color: "text-blue-700",
        bg: "bg-blue-50",
        border: "border-blue-200",
        apiStatus: "atandi",
    },
    {
        key: "yapiliyor",
        label: "Yapılıyor",
        color: "text-yellow-700",
        bg: "bg-yellow-50",
        border: "border-yellow-200",
        apiStatus: "yapiliyor",
    },
    {
        key: "beklemede",
        label: "Beklemede",
        color: "text-orange-700",
        bg: "bg-orange-50",
        border: "border-orange-200",
        apiStatus: "beklemede",
    },
    {
        key: "pending_approval",
        label: "Onay Bekleyenler",
        color: "text-purple-700",
        bg: "bg-purple-50",
        border: "border-purple-200",
        apiStatus: "pending_approval",
        readOnly: true,
    },
    {
        key: "tamamlandi",
        label: "Tamamlandı",
        color: "text-green-700",
        bg: "bg-green-50",
        border: "border-green-200",
        apiStatus: "tamamlandi",
        readOnly: true,
    },
    {
        key: "iptal",
        label: "İptal",
        color: "text-red-700",
        bg: "bg-red-50",
        border: "border-red-200",
        apiStatus: "iptal_edildi",
    },
];

/** Görev kartına dnd-kit sürükleme durumunu taşıyan prop paketi. */
type TaskCardDragState = {
    setNodeRef?: (element: HTMLElement | null) => void;
    attributes?: DraggableAttributes;
    listeners?: ReturnType<typeof useDraggable>["listeners"];
    isDragging?: boolean;
};

/** Kanban sütununu dnd-kit droppable alanına çeviren sarmalayıcı. */
function KanbanDroppableColumn({
    columnKey,
    children,
}: {
    columnKey: KanbanStatus;
    children: (props: {
        setNodeRef: (element: HTMLElement | null) => void;
        isOver: boolean;
    }) => React.ReactNode;
}) {
    const { setNodeRef, isOver } = useDroppable({
        id: columnKey,
        data: { status: columnKey },
    });
    return <>{children({ setNodeRef, isOver })}</>;
}

/** Görev kartını dnd-kit draggable elemanına çeviren sarmalayıcı. */
function KanbanDraggableTask({
    taskId,
    status,
    disabled,
    children,
}: {
    taskId: number;
    status: KanbanStatus;
    disabled: boolean;
    children: (drag: TaskCardDragState) => React.ReactNode;
}) {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: String(taskId),
        data: { status },
        disabled,
    });
    return (
        <>{children({ setNodeRef, attributes, listeners, isDragging })}</>
    );
}

const STATUS_OPTIONS = [
    { value: "atandi", label: "Atandı" },
    { value: "yapiliyor", label: "Yapılıyor" },
    { value: "beklemede", label: "Beklemede" },
    { value: "tamamlandi", label: "Tamamlandı" },
    { value: "iptal_edildi", label: "İptal" },
    { value: "arsivlendi", label: "Arşiv" },
];

const PRIORITY_MAP: Record<string, string> = {
    dusuk: "Düşük",
    orta: "Orta",
    yuksek: "Yüksek",
    acil: "Acil",
};

const PRIORITY_COLORS: Record<string, string> = {
    dusuk: "bg-slate-100 text-slate-600",
    orta: "bg-blue-100 text-blue-700",
    yuksek: "bg-orange-100 text-orange-700",
    acil: "bg-red-100 text-red-700",
};

const SORT_OPTIONS: { value: SortField; label: string }[] = [
    { value: "created_at", label: "Oluşturulma Tarihi" },
    { value: "due_date", label: "Son Tarih" },
    { value: "priority", label: "Öncelik" },
    { value: "title", label: "Başlık" },
    { value: "status", label: "Statü" },
];

interface TaskWithExtras extends Task {
    assignees?: UserType[];
}

interface Filters {
    status: string;
    priority: string;
    assignee_id: string;
    date_from: string;
    date_to: string;
}

interface QuickFilter {
    key: string;
    label: string;
    params: Record<string, string>;
}

const QUICK_FILTERS: QuickFilter[] = [
    { key: "overdue", label: "Gecikmiş", params: { overdue: "true" } },
    { key: "due_today", label: "Bugün", params: { due_today: "true" } },
    {
        key: "due_this_week",
        label: "Bu Hafta",
        params: { due_this_week: "true" },
    },
];

export default function TasksPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { projects, selectedProject, setSelectedProject } = useProject();

    const [tasks, setTasks] = useState<TaskWithExtras[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTaskId, setActiveTaskId] = useState<number | null>(null);
    const dndSensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 8 },
        }),
        useSensor(KeyboardSensor),
    );
    const [overdueTasks, setOverdueTasks] = useState<TaskWithExtras[]>([]);
    const [showOverdueSection, setShowOverdueSection] = useState(true);
    const [completedTasks, setCompletedTasks] = useState<TaskWithExtras[]>([]);
    const [loadingCompleted, setLoadingCompleted] = useState(true);
    const [archivedTasks, setArchivedTasks] = useState<TaskWithExtras[]>([]);
    const [loadingArchived, setLoadingArchived] = useState(true);
    const [assignedToMeTasks, setAssignedToMeTasks] = useState<
        TaskWithExtras[]
    >([]);
    const [loadingAssignedToMe, setLoadingAssignedToMe] = useState(true);
    const [pendingApprovalTasks, setPendingApprovalTasks] = useState<
        TaskWithExtras[]
    >([]);
    const [loadingPendingApproval, setLoadingPendingApproval] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [editTask, setEditTask] = useState<TaskWithExtras | null>(null);
    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<Filters>({
        status: "",
        priority: "",
        assignee_id: "",
        date_from: "",
        date_to: "",
    });
    const [activeQuickFilters, setActiveQuickFilters] = useState<string[]>([]);
    const [showFilters, setShowFilters] = useState(false);
    const [users, setUsers] = useState<UserType[]>([]);
    const [groups, setGroups] = useState<UserGroup[]>([]);
    const [page, setPage] = useState(1);
    const [perPage] = useState(20);
    const [total, setTotal] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const fetchTasksRef = useRef<((targetPage: number, signal?: AbortSignal) => void) | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [viewMode, setViewMode] = useState<ViewMode>(
        (searchParams.get("view") as ViewMode) || "kanban",
    );
    const prevSearchRef = useRef(search);
    const prevViewModeRef = useRef(viewMode);
    const handleViewModeChange = useCallback(
        (mode: ViewMode) => {
            setViewMode(mode);
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set("view", mode);
                return next;
            });
        },
        [setSearchParams],
    );

    // Arama/huddle içinden "Görev Aç" (?new=1) ile gelindiyse görev formunu aç.
    useEffect(() => {
        if (searchParams.get("new") !== "1") return;
        if (hasPermission(user, "tasks", "create")) {
            setModalOpen(true);
        }
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.delete("new");
                return next;
            },
            { replace: true },
        );
    }, [searchParams, setSearchParams, user]);

    const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
    const [sort, setSort] = useState<SortField>("due_date");
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

    const normalizeStatus = (task: TaskWithExtras): KanbanStatus => {
        const status: string = task.status;
        if (status === "devam_ediyor") return "yapiliyor";
        if (status === "iptal_edildi" || status === "iptal") return "iptal";
        if (status === "tamamlandi") {
            return task.approval_status === "pending"
                ? "pending_approval"
                : "tamamlandi";
        }
        return status as KanbanStatus;
    };

    const buildParams = useCallback(
        (targetPage: number) => {
            const params = new URLSearchParams();
            if (selectedProject)
                params.append("project_id", String(selectedProject.id));
            if (filters.status) params.append("status", filters.status);
            if (filters.priority) params.append("priority", filters.priority);
            if (filters.assignee_id)
                params.append("assignee_id", filters.assignee_id);
            if (search) params.append("search", search);
            if (viewMode === "calendar") {
                // Takvim görevleri due_date'e göre gösterir; görünen ayın aralığını
                // çek (kullanıcı tarih filtreleriyle kesişim). Ay değişiminde yeniden çekilir.
                const year = currentMonth.getFullYear();
                const month = currentMonth.getMonth();
                const monthStr = String(month + 1).padStart(2, "0");
                const monthStart = `${year}-${monthStr}-01`;
                const lastDay = new Date(year, month + 1, 0).getDate();
                const monthEnd = `${year}-${monthStr}-${String(lastDay).padStart(2, "0")}`;
                params.append(
                    "date_from",
                    filters.date_from && filters.date_from > monthStart
                        ? filters.date_from
                        : monthStart,
                );
                params.append(
                    "date_to",
                    filters.date_to && filters.date_to < monthEnd
                        ? filters.date_to
                        : monthEnd,
                );
            } else {
                if (filters.date_from)
                    params.append("date_from", filters.date_from);
                if (filters.date_to) params.append("date_to", filters.date_to);
            }
            if (sort) params.append("sort", sort);
            params.append("direction", sortDirection);
            params.append("page", String(targetPage));
            params.append(
                "per_page",
                // Kanban ve takvimde sayfalama UI'ı yok; backend per_page'i
                // max 100 ile sınırladığı için 100'lük sayfalar fetchTasks
                // içinde birleştirilir (BOARD_MAX_TASKS tavanına kadar).
                viewMode === "kanban" || viewMode === "calendar"
                    ? String(BOARD_PAGE_SIZE)
                    : String(perPage),
            );

            activeQuickFilters.forEach((key) => {
                const qf = QUICK_FILTERS.find((f) => f.key === key);
                if (qf) {
                    Object.entries(qf.params).forEach(([k, v]) =>
                        params.append(k, v),
                    );
                }
            });

            return params;
        },
        [
            selectedProject,
            filters,
            search,
            sort,
            sortDirection,
            perPage,
            activeQuickFilters,
            viewMode,
            currentMonth,
        ],
    );

    const normalizeTask = (t: TaskWithExtras): TaskWithExtras => ({
        ...t,
        assignees: (t.assigned_users || []).map(
            (u) => ({ ...u, avatar: null }) as UserType,
        ),
    });

    const fetchTasks = useCallback(
        async (targetPage: number, signal?: AbortSignal) => {
            setLoading(true);
            setError(null);
            try {
                const isBoard =
                    viewMode === "kanban" || viewMode === "calendar";
                const params = buildParams(targetPage);
                const res = await api.get(
                    `/api/v1/tasks?${params.toString()}`,
                    { signal },
                );
                let data = (res.data?.data || []) as TaskWithExtras[];
                const meta = res.data?.meta || {};
                const total = meta.total || data.length;

                // Kanban/takvimde sayfalama UI'ı yok; kalan sayfalar
                // BOARD_MAX_TASKS tavanına kadar birleştirilir.
                if (
                    isBoard &&
                    total > data.length &&
                    data.length < BOARD_MAX_TASKS
                ) {
                    const lastPage = Math.min(
                        Math.ceil(total / BOARD_PAGE_SIZE),
                        Math.ceil(BOARD_MAX_TASKS / BOARD_PAGE_SIZE),
                    );
                    const requests = [];
                    for (let p = 2; p <= lastPage; p++) {
                        requests.push(
                            api.get(
                                `/api/v1/tasks?${buildParams(p).toString()}`,
                                { signal },
                            ),
                        );
                    }
                    const results = await Promise.all(requests);
                    for (const r of results) {
                        data = data.concat(
                            (r.data?.data || []) as TaskWithExtras[],
                        );
                    }
                    data = data.slice(0, BOARD_MAX_TASKS);
                }

                setTasks(data.map(normalizeTask));
                setTotal(total);
                setHasMore(!isBoard && data.length === perPage);
            } catch (err: unknown) {
                const e = err as { name?: string };
                if (e.name === "CanceledError" || e.name === "AbortError") {
                    return;
                }
                console.error("Hata:", err);
                setError(
                    "Veriler yüklenirken bir hata oluştu. Lütfen tekrar deneyin.",
                );
            } finally {
                setLoading(false);
            }
        },
        [buildParams, perPage, viewMode],
    );
    fetchTasksRef.current = fetchTasks;

    const fetchOverdueTasks = useCallback(
        async (signal?: AbortSignal) => {
            try {
                const params = new URLSearchParams();
                params.append("overdue", "true");
                if (selectedProject)
                    params.append("project_id", String(selectedProject.id));
                params.append("sort", "due_date");
                params.append("direction", "asc");
                params.append("per_page", "100");
                params.append("page", "1");

                const res = await api.get(
                    `/api/v1/tasks?${params.toString()}`,
                    { signal },
                );
                const data = (res.data?.data || []) as TaskWithExtras[];
                setOverdueTasks(data.map(normalizeTask));
            } catch (err: unknown) {
                const e = err as { name?: string };
                if (e.name === "CanceledError" || e.name === "AbortError") {
                    return;
                }
                console.error("Gecikmiş görevler yüklenirken hata:", err);
            }
        },
        [selectedProject],
    );

    const applyCommonFilters = (params: URLSearchParams) => {
        if (selectedProject)
            params.append("project_id", String(selectedProject.id));
        if (filters.priority) params.append("priority", filters.priority);
        if (filters.assignee_id)
            params.append("assignee_id", filters.assignee_id);
        if (filters.date_from) params.append("date_from", filters.date_from);
        if (filters.date_to) params.append("date_to", filters.date_to);
        if (search.trim()) params.append("search", search.trim());
        params.append("sort", sort);
        params.append("direction", sortDirection);
        params.append("per_page", "100");
        params.append("page", "1");
    };

    const fetchCompletedTasks = useCallback(
        async (signal?: AbortSignal) => {
            setLoadingCompleted(true);
            try {
                const params = new URLSearchParams();
                params.append("status", "completed");
                applyCommonFilters(params);

                const res = await api.get(
                    `/api/v1/tasks?${params.toString()}`,
                    { signal },
                );
                const data = (res.data?.data || []) as TaskWithExtras[];
                setCompletedTasks(data.map(normalizeTask));
            } catch (err: unknown) {
                const e = err as { name?: string };
                if (e.name === "CanceledError" || e.name === "AbortError") {
                    return;
                }
                console.error("Tamamlanan görevler yüklenirken hata:", err);
            } finally {
                setLoadingCompleted(false);
            }
        },
        [selectedProject, filters, search, sort, sortDirection],
    );

    const fetchArchivedTasks = useCallback(
        async (signal?: AbortSignal) => {
            setLoadingArchived(true);
            try {
                const params = new URLSearchParams();
                params.append("archived", "true");
                applyCommonFilters(params);

                const res = await api.get(
                    `/api/v1/tasks?${params.toString()}`,
                    { signal },
                );
                const data = (res.data?.data || []) as TaskWithExtras[];
                setArchivedTasks(data.map(normalizeTask));
            } catch (err: unknown) {
                const e = err as { name?: string };
                if (e.name === "CanceledError" || e.name === "AbortError") {
                    return;
                }
                console.error("Arşiv görevler yüklenirken hata:", err);
            } finally {
                setLoadingArchived(false);
            }
        },
        [selectedProject, filters, search, sort, sortDirection],
    );

    const fetchAssignedToMeTasks = useCallback(
        async (signal?: AbortSignal) => {
            setLoadingAssignedToMe(true);
            try {
                const params = new URLSearchParams();
                params.append("my_tasks", "true");
                applyCommonFilters(params);

                const res = await api.get(
                    `/api/v1/tasks?${params.toString()}`,
                    { signal },
                );
                const data = (res.data?.data || []) as TaskWithExtras[];
                setAssignedToMeTasks(data.map(normalizeTask));
            } catch (err: unknown) {
                const e = err as { name?: string };
                if (e.name === "CanceledError" || e.name === "AbortError") {
                    return;
                }
                console.error("Bana atanan görevler yüklenirken hata:", err);
            } finally {
                setLoadingAssignedToMe(false);
            }
        },
        [selectedProject, filters, search, sort, sortDirection],
    );

    const fetchPendingApprovalTasks = useCallback(
        async (signal?: AbortSignal) => {
            setLoadingPendingApproval(true);
            try {
                const params = new URLSearchParams();
                params.append("status", "pending_approval");
                applyCommonFilters(params);

                const res = await api.get(
                    `/api/v1/tasks?${params.toString()}`,
                    { signal },
                );
                const data = (res.data?.data || []) as TaskWithExtras[];
                setPendingApprovalTasks(data.map(normalizeTask));
            } catch (err: unknown) {
                const e = err as { name?: string };
                if (e.name === "CanceledError" || e.name === "AbortError") {
                    return;
                }
                console.error("Onay bekleyen görevler yüklenirken hata:", err);
            } finally {
                setLoadingPendingApproval(false);
            }
        },
        [selectedProject, filters, search, sort, sortDirection],
    );

    useEffect(() => {
        // eslint-disable-next-line no-console
        console.log("page effect", page);
        const controller = new AbortController();
        fetchTasks(page, controller.signal);
        return () => controller.abort();
    }, [fetchTasks, page]);

    useEffect(() => {
        if (prevSearchRef.current === search) {
            return;
        }
        prevSearchRef.current = search;
        const controller = new AbortController();
        const timer = setTimeout(() => {
            setPage(1);
            fetchTasksRef.current?.(1, controller.signal);
        }, 300);
        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [search]);

    useEffect(() => {
        if (prevViewModeRef.current === viewMode) {
            return;
        }
        prevViewModeRef.current = viewMode;
        const controller = new AbortController();
        setPage(1);
        fetchTasksRef.current?.(1, controller.signal);
        return () => controller.abort();
    }, [viewMode]);

    useEffect(() => {
        const controller = new AbortController();
        fetchOverdueTasks(controller.signal);
        return () => controller.abort();
    }, [fetchOverdueTasks]);

    const specialViewModes = useMemo(
        () =>
            new Set<ViewMode>([
                "completed",
                "archived",
                "assigned-to-me",
                "pending_approval",
            ]),
        [],
    );

    useEffect(() => {
        if (!specialViewModes.has(viewMode)) {
            return;
        }
        const controller = new AbortController();
        fetchCompletedTasks(controller.signal);
        fetchArchivedTasks(controller.signal);
        fetchAssignedToMeTasks(controller.signal);
        fetchPendingApprovalTasks(controller.signal);
        return () => controller.abort();
    }, [
        viewMode,
        specialViewModes,
        fetchCompletedTasks,
        fetchArchivedTasks,
        fetchAssignedToMeTasks,
        fetchPendingApprovalTasks,
    ]);

    useEffect(() => {
        if (!specialViewModes.has(viewMode)) {
            return;
        }
        const controller = new AbortController();
        const timer = setTimeout(() => {
            fetchCompletedTasks(controller.signal);
            fetchArchivedTasks(controller.signal);
            fetchAssignedToMeTasks(controller.signal);
            fetchPendingApprovalTasks(controller.signal);
        }, 300);
        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [viewMode, specialViewModes, search, filters, sort, sortDirection, fetchCompletedTasks, fetchArchivedTasks, fetchAssignedToMeTasks, fetchPendingApprovalTasks]);

    useEffect(() => {
        const canManageAssignments =
            hasPermission(user, 'tasks', 'create') ||
            hasPermission(user, 'tasks', 'update');
        if (!canManageAssignments) {
            setUsers([]);
            setGroups([]);
            return;
        }

        const controller = new AbortController();
        const fetchUsers = async () => {
            try {
                const res = await api.get(
                    "/api/v1/users/search?search=&per_page=1000&exclude_self=false",
                    { signal: controller.signal },
                );
                setUsers(res.data?.data || []);
            } catch (err: unknown) {
                const e = err as { name?: string; response?: { status?: number } };
                if (e.name === "CanceledError" || e.name === "AbortError") {
                    return;
                }
                // Kullanici listesi yuklenemese bile modal calismaya devam etsin.
                setUsers([]);
            }
        };
        const fetchGroups = async () => {
            try {
                const res = await api.get("/api/v1/user-groups?per_page=100", {
                    signal: controller.signal,
                });
                setGroups(res.data?.data || []);
            } catch (err: unknown) {
                const e = err as { name?: string };
                if (e.name === "CanceledError" || e.name === "AbortError") {
                    return;
                }
                // Grup listesi yuklenemese bile modal calismaya devam etsin.
                setGroups([]);
            }
        };
        fetchUsers();
        fetchGroups();
        return () => controller.abort();
    }, [user]);

    const grouped = useMemo(() => {
        const map: Record<KanbanStatus, TaskWithExtras[]> = {
            atandi: [],
            yapiliyor: [],
            beklemede: [],
            pending_approval: [],
            tamamlandi: [],
            iptal: [],
        };
        const overdueIds = new Set(overdueTasks.map((t) => t.id));
        for (const t of tasks) {
            // Gecikmiş görevler ayrı bölümde gösterildiği için board sütunlarında tekrar çıkmasın.
            if (overdueIds.has(t.id)) continue;
            const col = normalizeStatus(t);
            map[col].push(t);
        }
        return map;
    }, [tasks, overdueTasks]);

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        setActiveTaskId(null);
        if (!over) return;
        const sourceStatus = active.data.current?.status as
            | KanbanStatus
            | undefined;
        const destStatus = over.data.current?.status as
            | KanbanStatus
            | undefined;
        if (!sourceStatus || !destStatus || sourceStatus === destStatus)
            return;

        // Onay akışı sütunlarına (Onay Bekleyenler / Tamamlandı) sürükle-bırakla geçiş yapılamaz;
        // bu işlem ayrı onay endpoint’leri üzerinden yönetilir.
        // Aynı şekilde bu sütunlardan çıkış da sürükle-bırakla yapılamaz.
        if (
            ["pending_approval", "tamamlandi"].includes(sourceStatus) ||
            ["pending_approval", "tamamlandi"].includes(destStatus)
        )
            return;

        const taskId = Number(active.id);
        const targetApiStatus = KANBAN_COLUMNS.find(
            (c) => c.key === destStatus,
        )?.apiStatus;
        if (!targetApiStatus) return;

        setTasks((prev) =>
            prev.map((t) =>
                t.id === taskId
                    ? {
                          ...t,
                          status: targetApiStatus as TaskWithExtras["status"],
                      }
                    : t,
            ),
        );

        try {
            await api.patch(`/api/v1/tasks/${taskId}/status`, {
                status: targetApiStatus,
            });
            fetchOverdueTasks();
            fetchCompletedTasks();
            fetchArchivedTasks();
            fetchPendingApprovalTasks();
        } catch (err) {
            console.error("Statü güncelleme hatası:", err);
            setError(
                "Görev statüsü güncellenirken bir hata oluştu. Sayfayı yenileyin.",
            );
            fetchTasks(page);
        }
    };

    const isOverdue = (task: TaskWithExtras) => {
        if (task.is_overdue !== undefined) return task.is_overdue;
        if (
            !task.due_date ||
            task.status === "tamamlandi" ||
            task.status === "iptal_edildi" ||
            task.status === "arsivlendi"
        )
            return false;
        const due = new Date(task.due_date);
        const today = new Date();
        due.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        return due < today;
    };

    const matchesFilters = useCallback(
        (task: TaskWithExtras): boolean => {
            if (selectedProject && task.project_id !== selectedProject.id)
                return false;
            if (filters.status && task.status !== filters.status) return false;
            if (filters.priority && task.priority !== filters.priority)
                return false;
            if (filters.assignee_id) {
                const assigneeIds = (task.assigned_users || []).map((u) =>
                    String(u.id),
                );
                if (!assigneeIds.includes(filters.assignee_id)) return false;
            }
            if (search.trim()) {
                const q = search.toLowerCase();
                if (
                    !task.title.toLowerCase().includes(q) &&
                    !(task.description ?? "").toLowerCase().includes(q)
                )
                    return false;
            }
            for (const key of activeQuickFilters) {
                const qf = QUICK_FILTERS.find((f) => f.key === key);
                if (!qf) continue;
                const params = qf.params;
                if (params.overdue === "true") {
                    if (!isOverdue(task)) return false;
                }
                if (params.due_today === "true") {
                    if (
                        !task.due_date ||
                        new Date(task.due_date).toDateString() !==
                            new Date().toDateString()
                    )
                        return false;
                }
                if (params.due_this_week === "true") {
                    if (!task.due_date) return false;
                    const now = new Date();
                    const dayOfWeek = (now.getDay() + 6) % 7; // Pazartesi=0
                    const startOfWeek = new Date(now);
                    startOfWeek.setDate(now.getDate() - dayOfWeek);
                    startOfWeek.setHours(0, 0, 0, 0);
                    const endOfWeek = new Date(startOfWeek);
                    endOfWeek.setDate(startOfWeek.getDate() + 6);
                    endOfWeek.setHours(23, 59, 59, 999);
                    const due = new Date(task.due_date);
                    if (due < startOfWeek || due > endOfWeek) return false;
                }
            }
            return true;
        },
        [
            selectedProject,
            filters,
            search,
            activeQuickFilters,
            user?.id,
            user?.groups,
        ],
    );

    useRealtimeTasks(selectedProject?.id, {
        onTaskCreated: (task) => {
            setTasks((prev) => {
                if (prev.some((t) => t.id === task.id)) return prev;
                const enriched: TaskWithExtras = {
                    ...task,
                    assignees: (task.assigned_users || []).map(
                        (u) => ({ ...u, avatar: null }) as UserType,
                    ),
                };
                if (!matchesFilters(enriched)) return prev;
                return [enriched, ...prev].slice(0, perPage);
            });
            setTotal((prev) => prev + 1);
            fetchOverdueTasks();
            fetchCompletedTasks();
            fetchArchivedTasks();
            fetchPendingApprovalTasks();
        },
        onTaskUpdated: (task) => {
            setTasks((prev) => {
                const exists = prev.some((t) => t.id === task.id);
                if (exists) {
                    return prev.map((t) =>
                        t.id === task.id
                            ? {
                                  ...task,
                                  assignees: (task.assigned_users || []).map(
                                      (u) =>
                                          ({ ...u, avatar: null }) as UserType,
                                  ),
                              }
                            : t,
                    );
                }
                const enriched: TaskWithExtras = {
                    ...task,
                    assignees: (task.assigned_users || []).map(
                        (u) => ({ ...u, avatar: null }) as UserType,
                    ),
                };
                if (!matchesFilters(enriched)) return prev;
                return [enriched, ...prev].slice(0, perPage);
            });
            fetchOverdueTasks();
            fetchCompletedTasks();
            fetchArchivedTasks();
            fetchPendingApprovalTasks();
        },
        onTaskDeleted: (taskId) => {
            setTasks((prev) => prev.filter((t) => t.id !== taskId));
            setTotal((prev) => Math.max(0, prev - 1));
            fetchOverdueTasks();
            fetchCompletedTasks();
            fetchArchivedTasks();
            fetchPendingApprovalTasks();
        },
        onTaskStatusChanged: (taskId, payload) => {
            setTasks((prev) =>
                prev.map((t) =>
                    t.id === taskId
                        ? {
                              ...t,
                              status: payload.new_status as TaskWithExtras["status"],
                              approval_status:
                                  payload.approval_status as TaskWithExtras["approval_status"],
                              approved_by: payload.approved_by,
                              approved_at: payload.approved_at,
                          }
                        : t,
                ),
            );
        },
        onSubTaskCreated: () => fetchTasks(page),
        onSubTaskToggled: () => fetchTasks(page),
        onTaskCommentCreated: (taskId) => {
            setTasks((prev) =>
                prev.map((t) =>
                    t.id === taskId
                        ? { ...t, comments_count: (t.comments_count || 0) + 1 }
                        : t,
                ),
            );
        },
    });

    const handleFilterChange = (key: keyof Filters, value: string) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setPage(1);
    };

    const clearFilters = () => {
        setFilters({
            status: "",
            priority: "",
            assignee_id: "",
            date_from: "",
            date_to: "",
        });
        setActiveQuickFilters([]);
        setSearch("");
        setPage(1);
    };

    const toggleQuickFilter = (key: string) => {
        setActiveQuickFilters((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
        );
        setPage(1);
    };

    const removeQuickFilter = (key: string) => {
        setActiveQuickFilters((prev) => prev.filter((k) => k !== key));
        setPage(1);
    };

    const activeFilterCount =
        Object.values(filters).filter(Boolean).length +
        activeQuickFilters.length +
        (search ? 1 : 0);

    const handleProjectChange = (projectId: string) => {
        const project = projects.find((p) => String(p.id) === projectId);
        setSelectedProject(project || null);
        setPage(1);
    };

    const handleSortChange = (field: SortField) => {
        if (sort === field) {
            setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
        } else {
            setSort(field);
            setSortDirection(
                field === "due_date" || field === "priority" ? "asc" : "desc",
            );
        }
        setPage(1);
    };

    const openEditModal = (task: TaskWithExtras) => {
        setEditTask(task);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditTask(null);
    };

    const renderTaskCard = (
        task: TaskWithExtras,
        drag?: TaskCardDragState,
        readOnly = false,
    ) => {
        const isReadOnly =
            readOnly ||
            task.is_readonly ||
            task.is_archived ||
            task.is_pending_approval;
        return (
            <div
                ref={drag?.setNodeRef}
                data-testid="task-card"
                data-taskid={task.id}
                onClick={() =>
                    !drag?.isDragging && navigate(`/tasks/${task.id}`)
                }
                className={`group relative cursor-pointer rounded-lg border bg-white p-[5px] text-left shadow-sm transition sm:p-2 lg:p-3 ${
                    isReadOnly ? "" : "hover:shadow-md"
                } ${isOverdue(task) ? "border-red-200 bg-red-50" : "border-slate-200"} ${
                    drag?.isDragging ? "opacity-40" : ""
                }`}
            >
                <div className="mb-0.5 flex items-start justify-between gap-1">
                    <div className="flex min-w-0 flex-1 items-start gap-1.5">
                        {!isReadOnly && drag && (
                            <div
                                {...(drag.attributes || {})}
                                {...(drag.listeners || {})}
                                data-testid={`kanban-drag-handle-${task.id}`}
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                className="mt-0.5 shrink-0 cursor-grab touch-none text-slate-500 hover:text-slate-600"
                                role="button"
                                aria-label="Sürükle"
                                tabIndex={0}
                            >
                                <GripVertical className="h-3.5 w-3.5 sm:h-4 sm:w-4 lg:h-5 lg:w-5" />
                            </div>
                        )}
                        <h4
                            className="cursor-pointer text-[11px] font-semibold leading-tight text-slate-900 line-clamp-2 sm:text-[12px] lg:text-[15px]"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (!drag?.isDragging)
                                    navigate(`/tasks/${task.id}`);
                            }}
                        >
                            {task.title}
                        </h4>
                        {task.is_unread && (
                            <span
                                data-testid="task-card-unread-badge"
                                className="shrink-0 rounded-full bg-red-500 px-1 py-0 text-[9px] font-bold text-white sm:text-[10px] lg:text-xs"
                            >
                                Yeni
                            </span>
                        )}
                    </div>
                    {!isReadOnly && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                openEditModal(task);
                            }}
                            className="rounded p-1 text-slate-500 opacity-100 hover:bg-slate-100 hover:text-slate-700 focus:opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
                            title="Düzenle"
                        >
                            <Pencil className="h-3.5 w-3.5 sm:h-4 sm:w-4 lg:h-5 lg:w-5" />
                        </button>
                    )}
                </div>

                {task.project && (
                    <div className="mb-0 flex items-center gap-1 text-[10px] text-slate-500 sm:text-[10px] lg:text-xs">
                        <Folder className="h-3 w-3 lg:h-4 lg:w-4" />
                        <span className="truncate">{task.project.name}</span>
                    </div>
                )}
                {task.creator && (
                    <div
                        className="mb-0 flex items-center gap-1 text-[10px] text-slate-500 sm:text-[10px] lg:text-xs"
                        data-testid="task-card-creator"
                        title={`Oluşturan: ${task.creator.name} ${task.creator.surname}`}
                    >
                        <span>Olu:</span>
                        <span className="truncate font-medium text-slate-700">
                            {task.creator.name} {task.creator.surname}
                        </span>
                    </div>
                )}

                <div className="mb-0 flex flex-wrap items-center gap-1">
                    <span
                        className={`rounded px-1 py-0 text-[10px] font-bold uppercase sm:text-[10px] lg:text-xs ${PRIORITY_COLORS[task.priority] || "bg-slate-100"}`}
                    >
                        {PRIORITY_MAP[task.priority] || task.priority}
                    </span>
                    {isOverdue(task) && (
                        <span className="flex items-center gap-0.5 rounded bg-red-100 px-1 py-0 text-[10px] font-bold uppercase text-red-700 sm:text-[10px] lg:text-xs">
                            <AlertTriangle className="h-3 w-3 lg:h-4 lg:w-4" />
                            Gecikti
                        </span>
                    )}
                </div>

                <div className="flex items-center justify-between text-[10px] text-slate-500 sm:text-[10px] lg:text-xs">
                    <div className="flex -space-x-1">
                        {(task.assignees || []).slice(0, 3).map((u) =>
                            u.avatar ? (
                                <img
                                    key={u.id}
                                    src={u.avatar}
                                    alt=""
                                    className="h-4 w-4 rounded-full border border-white object-cover sm:h-5 sm:w-5 lg:h-6 lg:w-6"
                                />
                            ) : (
                                <div
                                    key={u.id}
                                    className="flex h-4 w-4 items-center justify-center rounded-full border border-white bg-blue-100 text-[10px] font-semibold text-blue-700 sm:h-5 sm:w-5 sm:text-[10px] lg:h-6 lg:w-6 lg:text-xs"
                                >
                                    {u.name?.[0] || "?"}
                                </div>
                            ),
                        )}
                        {(task.assignees || []).length > 3 && (
                            <div className="flex h-4 w-4 items-center justify-center rounded-full border border-white bg-slate-100 text-[10px] font-semibold text-slate-600 sm:h-5 sm:w-5 sm:text-[10px] lg:h-6 lg:w-6 lg:text-xs">
                                +{(task.assignees || []).length - 3}
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        {typeof task.comments_count === "number" &&
                            task.comments_count > 0 && (
                                <span
                                    className="flex items-center gap-0.5"
                                    title="Yorumlar"
                                    data-testid="task-card-comments-count"
                                >
                                    <MessageSquare className="h-3 w-3 lg:h-4 lg:w-4" />
                                    {task.comments_count}
                                </span>
                            )}
                        {typeof task.attachments_count === "number" &&
                            task.attachments_count > 0 && (
                                <span
                                    className="flex items-center gap-0.5"
                                    title="Ekler"
                                    data-testid="task-card-attachments-count"
                                >
                                    <Paperclip className="h-3 w-3 lg:h-4 lg:w-4" />
                                    {task.attachments_count}
                                </span>
                            )}
                        {task.due_date && (
                            <span
                                className={`flex items-center gap-0.5 ${isOverdue(task) ? "font-semibold text-red-600" : ""}`}
                            >
                                <Calendar className="h-3 w-3 lg:h-4 lg:w-4" />
                                {new Date(task.due_date).toLocaleDateString(
                                    "tr-TR",
                                )}
                            </span>
                        )}
                        {typeof task.sub_tasks_count === "number" &&
                            task.sub_tasks_count > 0 && (
                                <span
                                    className="flex items-center gap-0.5"
                                    data-testid="task-card-subtasks-count"
                                >
                                    <CheckSquare className="h-3 w-3 lg:h-4 lg:w-4" />
                                    {task.sub_tasks_count}
                                </span>
                            )}
                    </div>
                </div>
                {typeof task.sub_tasks_count === "number" &&
                    task.sub_tasks_count > 0 && (
                        <div className="mt-1" data-testid="task-card-progress">
                            <div className="mb-0.5 flex justify-between text-[9px] text-slate-500 lg:text-xs">
                                <span>İlerleme</span>
                                <span>{task.completion_percentage ?? 0}%</span>
                            </div>
                            <div className="h-1 w-full overflow-hidden rounded-full bg-slate-100 lg:h-1.5">
                                <div
                                    className="h-full rounded-full bg-blue-500 transition-all"
                                    style={{ width: `${task.completion_percentage ?? 0}%` }}
                                />
                            </div>
                        </div>
                    )}
            </div>
        );
    };

    const renderListRow = (task: TaskWithExtras) => {
        const isReadOnly = task.is_readonly || task.is_archived;
        return (
            <div
                key={task.id}
                data-testid="task-list-row"
                onClick={() => navigate(`/tasks/${task.id}`)}
                className={`grid cursor-pointer grid-cols-12 items-center gap-3 rounded-lg border bg-white px-3 py-3 text-xs transition hover:shadow-sm sm:px-4 sm:py-4 sm:text-sm ${
                    isOverdue(task)
                        ? "border-red-200 bg-red-50"
                        : "border-slate-200"
                }`}
            >
                <div className="col-span-6 sm:col-span-4">
                    <div className="flex items-center gap-2">
                        <div className="text-[13px] font-medium text-slate-900 line-clamp-1 sm:text-sm">
                            {task.title}
                        </div>
                        {task.is_unread && (
                            <span
                                data-testid="task-list-unread-badge"
                                className="shrink-0 rounded-full bg-red-500 px-1 py-0 text-[9px] font-bold text-white sm:px-1.5 sm:py-0.5 sm:text-[10px]"
                            >
                                Yeni
                            </span>
                        )}
                    </div>
                    {task.project && (
                        <div className="text-xs text-slate-500">
                            {task.project.name}
                        </div>
                    )}
                    {typeof task.sub_tasks_count === "number" &&
                        task.sub_tasks_count > 0 && (
                            <div className="mt-1.5 w-full max-w-[160px]">
                                <div className="mb-0.5 flex justify-between text-[10px] text-slate-500">
                                    <span>İlerleme</span>
                                    <span>{task.completion_percentage ?? 0}%</span>
                                </div>
                                <div className="h-1 w-full overflow-hidden rounded-full bg-slate-100">
                                    <div
                                        className="h-full rounded-full bg-blue-500 transition-all"
                                        style={{ width: `${task.completion_percentage ?? 0}%` }}
                                    />
                                </div>
                            </div>
                        )}
                </div>
                <div className="col-span-6 flex items-center gap-2 sm:hidden">
                    <span
                        className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${PRIORITY_COLORS[task.priority] || "bg-slate-100"}`}
                    >
                        {PRIORITY_MAP[task.priority] || task.priority}
                    </span>
                    <div className="flex -space-x-1.5">
                        {(task.assignees || []).slice(0, 2).map((u) => (
                            <div
                                key={u.id}
                                className="flex h-4 w-4 items-center justify-center rounded-full border border-white bg-blue-100 text-[10px] font-semibold text-blue-700 sm:h-5 sm:w-5 sm:text-[10px]"
                            >
                                {u.name?.[0] || "?"}
                            </div>
                        ))}
                        {(task.assignees || []).length > 2 && (
                            <div className="flex h-4 w-4 items-center justify-center rounded-full border border-white bg-slate-100 text-[10px] font-semibold text-slate-600 sm:h-5 sm:w-5 sm:text-[10px]">
                                +{(task.assignees || []).length - 2}
                            </div>
                        )}
                    </div>
                </div>
                <div className="col-span-2 hidden sm:block">
                    <span
                        className={`inline-flex rounded px-2 py-0.5 text-xs font-semibold uppercase ${PRIORITY_COLORS[task.priority] || "bg-slate-100"}`}
                    >
                        {PRIORITY_MAP[task.priority] || task.priority}
                    </span>
                </div>
                <div className="col-span-2 hidden sm:block text-slate-700">
                    {STATUS_OPTIONS.find((s) => s.value === task.status)
                        ?.label || task.status}
                </div>
                <div className="col-span-2 hidden sm:block">
                    <div className="flex -space-x-1.5">
                        {(task.assignees || []).slice(0, 2).map((u) => (
                            <div
                                key={u.id}
                                className="flex h-6 w-6 items-center justify-center rounded-full border border-white bg-blue-100 text-[10px] font-semibold text-blue-700"
                            >
                                {u.name?.[0] || "?"}
                            </div>
                        ))}
                        {(task.assignees || []).length > 2 && (
                            <div className="flex h-6 w-6 items-center justify-center rounded-full border border-white bg-slate-100 text-[10px] font-semibold text-slate-600">
                                +{(task.assignees || []).length - 2}
                            </div>
                        )}
                    </div>
                </div>
                <div className="col-span-4 sm:col-span-2 flex items-center justify-between gap-2 text-xs text-slate-500">
                    <span
                        className={
                            isOverdue(task) ? "font-semibold text-red-600" : ""
                        }
                    >
                        {task.due_date
                            ? new Date(task.due_date).toLocaleDateString(
                                  "tr-TR",
                              )
                            : "-"}
                    </span>
                    {!isReadOnly && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                openEditModal(task);
                            }}
                            className="rounded p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700 sm:p-2"
                            title="Düzenle"
                        >
                            <Pencil className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>
            </div>
        );
    };

    const tasksByDate = useMemo(() => {
        const map: Record<string, TaskWithExtras[]> = {};
        for (const t of tasks) {
            if (!t.due_date) continue;
            const d = new Date(t.due_date);
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
            if (!map[key]) map[key] = [];
            map[key].push(t);
        }
        return map;
    }, [tasks]);

    const groupTasksByCompletedDate = (taskList: TaskWithExtras[]) => {
        const map: Record<string, TaskWithExtras[]> = {};
        for (const t of taskList) {
            const stamp = t.completed_at ?? t.updated_at;
            if (!stamp) continue;
            const key = toLocalDateKeyTR(stamp);
            if (!map[key]) map[key] = [];
            map[key].push(t);
        }
        return Object.entries(map)
            .sort(([a], [b]) => b.localeCompare(a))
            .map(([date, items]) => ({ date, tasks: items }));
    };

    const completedTasksByDate = useMemo(
        () => groupTasksByCompletedDate(completedTasks),
        [completedTasks],
    );
    const archivedTasksByDate = useMemo(
        () => groupTasksByCompletedDate(archivedTasks),
        [archivedTasks],
    );

    const formatCompletedDate = (dateStr: string) => {
        // dateStr, toLocalDateKeyTR'den gelen TR gün anahtarı (YYYY-MM-DD) — yerel parse et.
        const date = new Date(`${dateStr}T00:00:00`);
        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(today.getDate() - 1);

        const isSameDay = (a: Date, b: Date) =>
            a.getFullYear() === b.getFullYear() &&
            a.getMonth() === b.getMonth() &&
            a.getDate() === b.getDate();

        if (isSameDay(date, today)) return "Bugün";
        if (isSameDay(date, yesterday)) return "Dün";
        return date.toLocaleDateString("tr-TR", {
            day: "numeric",
            month: "long",
            year: "numeric",
        });
    };

    const renderGroupedByCompletedDate = (
        taskGroups: { date: string; tasks: TaskWithExtras[] }[],
        testId: string,
        emptyTitle: string,
        emptyDescription: string,
        colorClass: string,
        badgeClass: string,
    ) => (
        <div data-testid={testId} className="space-y-4">
            {taskGroups.length === 0 ? (
                <EmptyState
                    icon={CheckCircle2}
                    title={emptyTitle}
                    description={emptyDescription}
                />
            ) : (
                taskGroups.map(({ date, tasks: dateTasks }) => (
                    <div
                        key={date}
                        data-testid={`${testId}-group-${date}`}
                        className="space-y-2"
                    >
                        <div
                            className={`flex items-center justify-between rounded-lg px-3 py-2 ${colorClass}`}
                        >
                            <h3 className="text-sm font-bold">
                                {formatCompletedDate(date)}
                            </h3>
                            <span
                                className={`rounded-full px-2 py-0.5 text-xs font-bold ${badgeClass}`}
                            >
                                {dateTasks.length}
                            </span>
                        </div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            {dateTasks.map((task) => (
                                <div key={task.id}>
                                    {renderTaskCard(task, undefined, true)}
                                </div>
                            ))}
                        </div>
                    </div>
                ))
            )}
        </div>
    );

    const renderAssignedToMeView = () => (
        <div data-testid="assigned-to-me-tasks-view" className="space-y-4">
            {assignedToMeTasks.length === 0 ? (
                <EmptyState
                    icon={UserCheck}
                    title="Size atanmış aktif görev yok"
                    description="Şu anda size atanmış devam eden görev bulunmuyor."
                />
            ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {assignedToMeTasks.map((task) => (
                        <div key={task.id}>{renderTaskCard(task)}</div>
                    ))}
                </div>
            )}
        </div>
    );

    const renderPendingApprovalView = () => (
        <div data-testid="pending-approval-tasks-view" className="space-y-4">
            {pendingApprovalTasks.length === 0 ? (
                <EmptyState
                    icon={Clock}
                    title="Onay bekleyen görev yok"
                    description="Seçili kriterlere uygun onay bekleyen görev bulunmuyor."
                />
            ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {pendingApprovalTasks.map((task) => (
                        <div key={task.id}>{renderTaskCard(task)}</div>
                    ))}
                </div>
            )}
        </div>
    );

    const renderCalendar = () => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const startOfMonth = new Date(year, month, 1);
        const endOfMonth = new Date(year, month + 1, 0);
        const daysInMonth = endOfMonth.getDate();
        const startDay = startOfMonth.getDay();
        const offset = startDay === 0 ? 6 : startDay - 1;
        const totalCells = Math.ceil((daysInMonth + offset) / 7) * 7;
        const weekDays = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

        const cells: JSX.Element[] = [];
        for (let i = 0; i < totalCells; i++) {
            const dayNumber = i - offset + 1;
            if (dayNumber < 1 || dayNumber > daysInMonth) {
                cells.push(
                    <div
                        key={`empty-${i}`}
                        className="min-h-[96px] bg-slate-50/50 sm:min-h-[120px]"
                    />,
                );
            } else {
                const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(dayNumber).padStart(2, "0")}`;
                const dayTasks = tasksByDate[dateStr] || [];
                const todayStr = new Date()
                    .toLocaleDateString("tr-TR", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                    })
                    .split(".")
                    .reverse()
                    .join("-");
                const isToday = dateStr === todayStr;
                cells.push(
                    <div
                        key={dateStr}
                        className={`flex min-h-[96px] flex-col gap-1 rounded-lg border p-2 sm:min-h-[120px] ${
                            isToday
                                ? "border-blue-300 bg-blue-50/50"
                                : "border-slate-200 bg-white"
                        }`}
                    >
                        <div
                            data-testid={`calendar-day-${dayNumber}`}
                            className={`text-right text-xs font-semibold ${isToday ? "text-blue-700" : "text-slate-500"}`}
                        >
                            {dayNumber}
                        </div>
                        <div className="flex flex-1 flex-col gap-1 overflow-y-auto">
                            {dayTasks.map((t) => (
                                <button
                                    key={t.id}
                                    onClick={() => navigate(`/tasks/${t.id}`)}
                                    title={t.title}
                                    className={`min-h-[24px] truncate rounded px-1.5 py-1 text-left text-xs font-medium ${
                                        t.status === "tamamlandi"
                                            ? "bg-green-100 text-green-700"
                                            : t.status === "iptal_edildi"
                                              ? "bg-red-100 text-red-700"
                                              : PRIORITY_COLORS[t.priority] ||
                                                "bg-slate-100 text-slate-700"
                                    }`}
                                >
                                    {t.title}
                                </button>
                            ))}
                        </div>
                    </div>,
                );
            }
        }

        return (
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <h3 className="text-base font-bold text-slate-900">
                        {currentMonth.toLocaleString("tr-TR", {
                            month: "long",
                            year: "numeric",
                        })}
                    </h3>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() =>
                                setCurrentMonth(
                                    (prev) =>
                                        new Date(
                                            prev.getFullYear(),
                                            prev.getMonth() - 1,
                                            1,
                                        ),
                                )
                            }
                            className="rounded-lg border border-slate-300 bg-white p-2 text-slate-600 hover:bg-slate-50"
                        >
                            <ChevronLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        </button>
                        <button
                            onClick={() => setCurrentMonth(new Date())}
                            data-testid="calendar-today-button"
                            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
                        >
                            Bugün
                        </button>
                        <button
                            onClick={() =>
                                setCurrentMonth(
                                    (prev) =>
                                        new Date(
                                            prev.getFullYear(),
                                            prev.getMonth() + 1,
                                            1,
                                        ),
                                )
                            }
                            className="rounded-lg border border-slate-300 bg-white p-2 text-slate-600 hover:bg-slate-50"
                        >
                            <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        </button>
                    </div>
                </div>
                <div className="grid grid-cols-7 gap-2">
                    {weekDays.map((d) => (
                        <div
                            key={d}
                            className="py-1 text-center text-xs font-bold uppercase text-slate-500"
                        >
                            {d}
                        </div>
                    ))}
                </div>
                <div className="grid grid-cols-7 gap-2">{cells}</div>
            </div>
        );
    };

    return (
        <div data-testid="tasks-page" className="space-y-4">
            {/* Üst bar */}
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-1 flex-wrap items-center gap-2 min-w-0">
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-slate-500 sm:left-2.5 sm:top-2.5 sm:h-4 sm:w-4" />
                        <input
                            type="text"
                            placeholder="Görev ara..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            data-testid="tasks-search-input"
                            className="min-h-[44px] w-full rounded-lg border border-slate-300 bg-white py-1.5 pl-8 pr-2 text-xs outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 sm:py-2 sm:pl-9 sm:pr-3 sm:text-sm"
                        />
                    </div>

                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={`flex min-h-[44px] items-center gap-1.5 rounded-lg border px-2 py-1.5 text-xs font-medium transition sm:px-3 sm:py-2 sm:text-sm ${
                            showFilters || activeFilterCount > 0
                                ? "border-blue-300 bg-blue-50 text-blue-700"
                                : "border-slate-300 bg-white text-slate-700"
                        }`}
                    >
                        <Filter className="h-4 w-4" />
                        Filtre
                        {activeFilterCount > 0 && (
                            <span className="ml-1 flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-[9px] text-white sm:h-5 sm:w-5 sm:text-[10px]">
                                {activeFilterCount}
                            </span>
                        )}
                    </button>

                    <div className="flex items-center gap-1 overflow-x-auto whitespace-nowrap rounded-lg border border-slate-300 bg-white p-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        {[
                            { key: "kanban", label: "Kanban", icon: LayoutGrid, color: "blue" },
                            { key: "list", label: "Liste", icon: List, color: "blue" },
                            { key: "calendar", label: "Takvim", icon: Calendar, color: "blue" },
                            { key: "lookahead", label: "Lookahead", icon: CalendarRange, color: "blue" },
                            { key: "completed", label: "Tamamlananlar", icon: CheckCircle2, color: "green" },
                            { key: "assigned-to-me", label: "Bana Atananlar", icon: UserCheck, color: "purple" },
                            { key: "pending_approval", label: "Onay Bekleyenler", icon: Clock, color: "yellow" },
                            { key: "archived", label: "Arşiv", icon: Archive, color: "slate" },
                        ].map((btn) => {
                            const Icon = btn.icon;
                            const active = viewMode === btn.key;
                            const colorClass = {
                                blue: active ? "bg-blue-100 text-blue-700" : "text-slate-600 hover:bg-slate-50",
                                green: active ? "bg-green-100 text-green-700" : "text-slate-600 hover:bg-slate-50",
                                purple: active ? "bg-purple-100 text-purple-700" : "text-slate-600 hover:bg-slate-50",
                                yellow: active ? "bg-yellow-100 text-yellow-700" : "text-slate-600 hover:bg-slate-50",
                                slate: active ? "bg-slate-200 text-slate-800" : "text-slate-600 hover:bg-slate-50",
                            }[btn.color];
                            return (
                                <button
                                    key={btn.key}
                                    onClick={() => handleViewModeChange(btn.key as ViewMode)}
                                    data-testid={`${btn.key}-view-toggle`}
                                    className={`flex min-h-[44px] min-w-[44px] items-center justify-center gap-1.5 rounded px-1.5 py-1 text-xs sm:min-w-fit sm:px-2 sm:py-1.5 sm:text-sm ${colorClass}`}
                                    title={btn.label}
                                    aria-label={btn.label}
                                >
                                    <Icon className="h-4 w-4 sm:h-4 sm:w-4" />
                                    <span className="hidden sm:inline">{btn.label}</span>
                                </button>
                            );
                        })}
                    </div>

                    <div className="flex items-center gap-2">
                        <select
                            value={sort}
                            onChange={(e) =>
                                handleSortChange(e.target.value as SortField)
                            }
                            className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs outline-none focus:border-blue-500 sm:px-3 sm:py-2 sm:text-sm"
                            title="Sıralama"
                            data-testid="task-sort-select"
                        >
                            {SORT_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={() =>
                                setSortDirection((prev) =>
                                    prev === "asc" ? "desc" : "asc",
                                )
                            }
                            data-testid="sort-direction-toggle"
                            className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border border-slate-300 bg-white p-2 text-slate-600 hover:bg-slate-50"
                            title={sortDirection === "asc" ? "Artan" : "Azalan"}
                            aria-label={sortDirection === "asc" ? "Artan" : "Azalan"}
                        >
                            <ArrowUpDown
                                className={`h-4 w-4 ${sortDirection === "asc" ? "rotate-180" : ""}`}
                            />
                        </button>
                    </div>
                </div>

                {hasPermission(user, 'tasks', 'create') && (
                    <button
                        onClick={() => setModalOpen(true)}
                        data-testid="new-task-button"
                        className="flex min-h-[44px] shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-blue-700 sm:gap-1.5 sm:px-4 sm:py-2 sm:text-sm"
                    >
                        <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        Yeni Görev
                    </button>
                )}
            </div>

            {/* Hızlı filtreler */}
            {["kanban", "list", "calendar"].includes(viewMode) && (
            <div className="flex flex-wrap items-center gap-2 overflow-x-auto">
                {QUICK_FILTERS.map((qf) => {
                    const active = activeQuickFilters.includes(qf.key);
                    return (
                        <button
                            key={qf.key}
                            data-testid={`quick-filter-${qf.key}`}
                            onClick={() => toggleQuickFilter(qf.key)}
                            className={`flex min-h-[44px] items-center rounded-full border px-2 py-0.5 text-[10px] font-medium transition sm:px-3 sm:py-1 sm:text-xs ${
                                active
                                    ? "border-blue-300 bg-blue-50 text-blue-700"
                                    : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                            }`}
                        >
                            {active && (
                                <CheckSquare className="mr-1 inline h-3 w-3" />
                            )}
                            {qf.label}
                        </button>
                    );
                })}
            </div>
            )}

            {/* Filtreler */}
            {showFilters && (
                <div className="grid grid-cols-1 gap-2 rounded-xl border border-slate-200 bg-white p-3 sm:grid-cols-2 sm:gap-3 sm:p-4 lg:grid-cols-4">
                    <div>
                        <label className="mb-1 block text-xs font-medium text-slate-500">
                            Proje
                        </label>
                        <select
                            value={selectedProject?.id ?? ""}
                            onChange={(e) =>
                                handleProjectChange(e.target.value)
                            }
                            data-testid="project-filter"
                            className="w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-blue-500 sm:px-3 sm:py-2 sm:text-sm"
                        >
                            <option value="">Tüm Projeler</option>
                            {projects.map((p: Project) => (
                                <option key={p.id} value={p.id}>
                                    {p.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="mb-1 block text-xs font-medium text-slate-500">
                            Statü
                        </label>
                        <select
                            data-testid="status-filter"
                            value={filters.status}
                            onChange={(e) =>
                                handleFilterChange("status", e.target.value)
                            }
                            className="w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-blue-500 sm:px-3 sm:py-2 sm:text-sm"
                        >
                            <option value="">Tümü</option>
                            {STATUS_OPTIONS.map((s) => (
                                <option key={s.value} value={s.value}>
                                    {s.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="mb-1 block text-xs font-medium text-slate-500">
                            Öncelik
                        </label>
                        <select
                            value={filters.priority}
                            onChange={(e) =>
                                handleFilterChange("priority", e.target.value)
                            }
                            data-testid="priority-filter"
                            className="w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-blue-500 sm:px-3 sm:py-2 sm:text-sm"
                        >
                            <option value="">Tümü</option>
                            <option value="dusuk">Düşük</option>
                            <option value="orta">Orta</option>
                            <option value="yuksek">Yüksek</option>
                            <option value="acil">Acil</option>
                        </select>
                    </div>

                    <div>
                        <label className="mb-1 block text-xs font-medium text-slate-500">
                            Atanan
                        </label>
                        <select
                            value={filters.assignee_id}
                            onChange={(e) =>
                                handleFilterChange(
                                    "assignee_id",
                                    e.target.value,
                                )
                            }
                            data-testid="assignee-filter"
                            className="w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-blue-500 sm:px-3 sm:py-2 sm:text-sm"
                        >
                            <option value="">Tümü</option>
                            {users.map((u) => (
                                <option key={u.id} value={String(u.id)}>
                                    {u.name} {u.surname}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="mb-1 block text-xs font-medium text-slate-500">
                            Başlangıç Tarihi
                        </label>
                        <input
                            type="date"
                            value={filters.date_from}
                            onChange={(e) =>
                                handleFilterChange("date_from", e.target.value)
                            }
                            data-testid="date-from-filter"
                            className="w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-blue-500 sm:px-3 sm:py-2 sm:text-sm"
                        />
                    </div>

                    <div>
                        <label className="mb-1 block text-xs font-medium text-slate-500">
                            Bitiş Tarihi
                        </label>
                        <input
                            type="date"
                            value={filters.date_to}
                            onChange={(e) =>
                                handleFilterChange("date_to", e.target.value)
                            }
                            data-testid="date-to-filter"
                            className="w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-blue-500 sm:px-3 sm:py-2 sm:text-sm"
                        />
                    </div>

                    <div className="flex items-end sm:col-span-2 lg:col-span-2">
                        <button
                            onClick={clearFilters}
                            className="flex min-h-[44px] items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 sm:px-3 sm:py-2 sm:text-sm"
                        >
                            <X className="h-4 w-4" />
                            Tümünü Temizle
                        </button>
                    </div>
                </div>
            )}

            {/* Aktif filtre badge'leri */}
            {activeFilterCount > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                    {filters.status && (
                        <ActiveFilterBadge
                            dataTestId="active-filter-status"
                            label={`Statü: ${STATUS_OPTIONS.find((s) => s.value === filters.status)?.label}`}
                            onRemove={() => handleFilterChange("status", "")}
                        />
                    )}
                    {filters.priority && (
                        <ActiveFilterBadge
                            dataTestId="active-filter-priority"
                            label={`Öncelik: ${PRIORITY_MAP[filters.priority]}`}
                            onRemove={() => handleFilterChange("priority", "")}
                        />
                    )}
                    {filters.assignee_id && (
                        <ActiveFilterBadge
                            dataTestId="active-filter-assignee"
                            label={`Atanan: ${users.find((u) => String(u.id) === filters.assignee_id)?.name || ""}`}
                            onRemove={() =>
                                handleFilterChange("assignee_id", "")
                            }
                        />
                    )}
                    {filters.date_from && (
                        <ActiveFilterBadge
                            dataTestId="active-filter-date-from"
                            label={`Başlangıç: ${filters.date_from}`}
                            onRemove={() => handleFilterChange("date_from", "")}
                        />
                    )}
                    {filters.date_to && (
                        <ActiveFilterBadge
                            dataTestId="active-filter-date-to"
                            label={`Bitiş: ${filters.date_to}`}
                            onRemove={() => handleFilterChange("date_to", "")}
                        />
                    )}
                    {activeQuickFilters.map((key) => {
                        const qf = QUICK_FILTERS.find((f) => f.key === key);
                        return qf ? (
                            <ActiveFilterBadge
                                key={key}
                                dataTestId={`active-quick-filter-${key}`}
                                label={qf.label}
                                onRemove={() => removeQuickFilter(key)}
                            />
                        ) : null;
                    })}
                    {search && (
                        <ActiveFilterBadge
                            dataTestId="active-filter-search"
                            label={`Ara: ${search}`}
                            onRemove={() => setSearch("")}
                        />
                    )}
                </div>
            )}

            {error && (
                <div data-testid="tasks-error" className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                    <span>{error}</span>
                    <button
                        type="button"
                        onClick={() => fetchTasks(page)}
                        data-testid="tasks-error-retry"
                        className="shrink-0 rounded-lg border border-red-300 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-100"
                    >
                        Tekrar Dene
                    </button>
                </div>
            )}

            {/* Sonuç bilgisi */}
            <div className="flex items-center justify-between text-xs text-slate-500">
                <span data-testid="tasks-total-count">Toplam {total} görev</span>
                {activeFilterCount > 0 && (
                    <span>{tasks.length} sonuç gösteriliyor</span>
                )}
            </div>

            {/* Gecikmiş görevler bölümü */}
            {overdueTasks.length > 0 && (
                <div
                    data-testid="overdue-section"
                    className="space-y-3 rounded-xl border border-red-200 bg-red-50/60 p-3 sm:p-4"
                >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-red-600" />
                            <h3 className="text-sm font-bold text-red-800">
                                Gecikmiş Görevler
                            </h3>
                            <span data-testid="overdue-section-count" className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-bold text-red-700">
                                {overdueTasks.length}
                            </span>
                        </div>
                        <button
                            data-testid="toggle-overdue-section"
                            onClick={() =>
                                setShowOverdueSection((prev) => !prev)
                            }
                            className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border border-red-200 bg-white p-1.5 text-red-700 hover:bg-red-100"
                            title={showOverdueSection ? "Daralt" : "Genişlet"}
                            aria-label={showOverdueSection ? "Daralt" : "Genişlet"}
                        >
                            {showOverdueSection ? (
                                <ChevronUp className="h-4 w-4" />
                            ) : (
                                <ChevronDown className="h-4 w-4" />
                            )}
                        </button>
                    </div>

                    {showOverdueSection && (
                        <div className="flex snap-x gap-3 overflow-x-auto pb-2">
                            {overdueTasks.map((task) => (
                                <div
                                    key={task.id}
                                    className="w-[78vw] max-w-[210px] shrink-0 snap-start sm:w-52 lg:w-64 lg:max-w-[280px]"
                                >
                                    {renderTaskCard(task)}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* İçerik */}
            {viewMode === "lookahead" ? (
                <LookaheadContent />
            ) : viewMode === "kanban" ? (
                loading && tasks.length === 0 ? (
                    <ListSkeleton variant="card" rows={5} testId="tasks-skeleton" />
                ) : !error && tasks.length === 0 ? (
                    <EmptyState
                        icon={CheckSquare}
                        title="Görev bulunamadı"
                        description="Arama kriterlerine uygun görev yok veya henüz görev oluşturulmadı."
                        testId="tasks-empty-state-kanban"
                    />
                ) : (
                    <>
                        <DndContext
                            sensors={dndSensors}
                            onDragStart={(event: DragStartEvent) =>
                                setActiveTaskId(Number(event.active.id))
                            }
                            onDragEnd={handleDragEnd}
                            onDragCancel={() => setActiveTaskId(null)}
                        >
                            <div className="overflow-x-hidden">
                                <div className="flex snap-x gap-3 overflow-x-auto pb-2">
                                    {KANBAN_COLUMNS.map((col) => (
                                        <KanbanDroppableColumn
                                            key={col.key}
                                            columnKey={col.key}
                                        >
                                            {({ setNodeRef, isOver }) => (
                                                <div
                                                    ref={setNodeRef}
                                                    data-testid={`kanban-column-${col.key}`}
                                                    className={`flex w-[42vw] max-w-[150px] shrink-0 snap-start flex-col rounded-lg transition sm:w-40 md:w-48 lg:w-72 lg:max-w-[300px] xl:w-80 xl:max-w-[320px] ${
                                                        isOver
                                                            ? "ring-2 ring-blue-300"
                                                            : ""
                                                    }`}
                                                >
                                                    <div
                                                        className={`mb-2 flex items-center justify-between rounded-lg border px-1.5 py-1 lg:px-2 ${col.bg} ${col.border}`}
                                                    >
                                                        <span
                                                            className={`text-[10px] font-bold uppercase tracking-wide sm:text-xs lg:text-sm ${col.color}`}
                                                        >
                                                            {col.label}
                                                        </span>
                                                        <span className="text-[10px] font-bold text-slate-500 sm:text-xs lg:text-sm">
                                                            {
                                                                grouped[col.key]
                                                                    .length
                                                            }
                                                        </span>
                                                    </div>
                                                    <div className="flex max-h-[65vh] flex-1 flex-col gap-1 overflow-y-auto pr-1">
                                                        {grouped[col.key].map(
                                                            (task) => (
                                                                <KanbanDraggableTask
                                                                    key={
                                                                        task.id
                                                                    }
                                                                    taskId={
                                                                        task.id
                                                                    }
                                                                    status={
                                                                        col.key
                                                                    }
                                                                    disabled={!!(task.is_readonly || task.is_archived)}
                                                                >
                                                                    {(drag) =>
                                                                        renderTaskCard(
                                                                            task,
                                                                            drag,
                                                                        )
                                                                    }
                                                                </KanbanDraggableTask>
                                                            ),
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </KanbanDroppableColumn>
                                    ))}
                                </div>
                            </div>
                            <DragOverlay dropAnimation={null}>
                                {activeTaskId ? (
                                    <div className="rounded-lg opacity-95 shadow-lg ring-2 ring-blue-200">
                                        {renderTaskCard(
                                            tasks.find(
                                                (t) => t.id === activeTaskId,
                                            )!,
                                        )}
                                    </div>
                                ) : null}
                            </DragOverlay>
                        </DndContext>

                        {/* Kanbanda sayfalama yok; tavan aşılırsa bilgi ver. */}
                        {total > tasks.length && (
                            <p
                                data-testid="tasks-kanban-truncated"
                                className="pt-2 text-center text-xs text-slate-500"
                            >
                                İlk {tasks.length} görev gösteriliyor (toplam{" "}
                                {total}).
                            </p>
                        )}
                    </>
                )
            ) : viewMode === "calendar" ? (
                loading && tasks.length === 0 ? (
                    <ListSkeleton variant="card" rows={5} testId="tasks-skeleton" />
                ) : (
                    // Ay aralığına göre çekim yapıldığı için boş ay da normaldir;
                    // ay navigasyonunun erişilebilir kalması için takvim her zaman render edilir.
                    <>
                        {renderCalendar()}
                        {total > tasks.length && (
                            <p
                                data-testid="tasks-calendar-truncated"
                                className="text-center text-xs text-slate-500"
                            >
                                Bu ayda tüm sonuçlar gösterilmiyor: ilk{" "}
                                {tasks.length} görev gösteriliyor (toplam{" "}
                                {total}).
                            </p>
                        )}
                    </>
                )
            ) : viewMode === "completed" ? (
                loadingCompleted ? (
                    <ListSkeleton variant="card" rows={5} testId="tasks-skeleton" />
                ) : (
                    renderGroupedByCompletedDate(
                        completedTasksByDate,
                        "completed-tasks-view",
                        "Tamamlanmış görev yok",
                        "Seçili kriterlere uygun tamamlanmış görev bulunmuyor.",
                        "bg-green-50",
                        "bg-green-100 text-green-700",
                    )
                )
            ) : viewMode === "archived" ? (
                loadingArchived ? (
                    <ListSkeleton variant="card" rows={5} testId="tasks-skeleton" />
                ) : (
                    renderGroupedByCompletedDate(
                        archivedTasksByDate,
                        "archived-tasks-view",
                        "Arşivlenmiş görev yok",
                        "Seçili kriterlere uygun arşivlenmiş görev bulunmuyor.",
                        "bg-slate-100",
                        "bg-slate-200 text-slate-700",
                    )
                )
            ) : viewMode === "assigned-to-me" ? (
                loadingAssignedToMe ? (
                    <ListSkeleton variant="card" rows={5} testId="tasks-skeleton" />
                ) : (
                    renderAssignedToMeView()
                )
            ) : viewMode === "pending_approval" ? (
                loadingPendingApproval ? (
                    <ListSkeleton variant="card" rows={5} testId="tasks-skeleton" />
                ) : (
                    renderPendingApprovalView()
                )
            ) : (
                <>
                    {loading && tasks.length === 0 ? (
                        <ListSkeleton variant="row" rows={8} testId="tasks-skeleton" />
                    ) : !error && tasks.length === 0 ? (
                        <EmptyState
                            icon={CheckSquare}
                            title="Görev bulunamadı"
                            description="Arama kriterlerine uygun görev yok veya henüz görev oluşturulmadı."
                        />
                    ) : (
                        <>
                            <div className="grid grid-cols-12 gap-3 rounded-lg bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600 sm:px-4 sm:py-2">
                                <div className="col-span-6 sm:col-span-4">
                                    Görev
                                </div>
                                <div className="col-span-2 hidden sm:block">
                                    Öncelik
                                </div>
                                <div className="col-span-2 hidden sm:block">
                                    Statü
                                </div>
                                <div className="col-span-2 hidden sm:block">
                                    Atanan
                                </div>
                                <div className="col-span-4 sm:col-span-2">
                                    Son Tarih
                                </div>
                            </div>
                            <div className="space-y-2">
                                {tasks.map(renderListRow)}
                            </div>

                            <Pagination
                                page={page}
                                hasMore={hasMore}
                                loading={loading}
                                onPageChange={setPage}
                            />
                        </>
                    )}
                </>
            )}

            {/* Modal */}
            {modalOpen && (
                <TaskModal
                    onClose={closeModal}
                    onCreated={() => {
                        setPage(1);
                        fetchTasks(1);
                        fetchOverdueTasks();
                        fetchCompletedTasks();
                        fetchPendingApprovalTasks();
                    }}
                    users={users}
                    groups={groups}
                    editTask={editTask}
                />
            )}
        </div>
    );
}

function Pagination({
    page,
    hasMore,
    loading,
    onPageChange,
}: {
    page: number;
    hasMore: boolean;
    loading: boolean;
    onPageChange: (page: number) => void;
}) {
    return (
        <div className="flex items-center justify-center gap-3 pt-2">
            <button
                onClick={() => onPageChange(Math.max(1, page - 1))}
                disabled={page <= 1 || loading}
                data-testid="pagination-prev"
                className="flex min-h-[44px] min-w-[44px] items-center justify-center gap-1 rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 sm:px-3 sm:py-2 sm:text-sm"
                aria-label="Önceki sayfa"
            >
                <ChevronLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Önceki</span>
            </button>
            <span data-testid="pagination-info" className="text-xs text-slate-600 sm:text-sm">Sayfa {page}</span>
            <button
                onClick={() => onPageChange(page + 1)}
                disabled={!hasMore || loading}
                data-testid="pagination-next"
                className="flex min-h-[44px] min-w-[44px] items-center justify-center gap-1 rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 sm:px-3 sm:py-2 sm:text-sm"
                aria-label="Sonraki sayfa"
            >
                <span className="hidden sm:inline">Sonraki</span>
                <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </button>
        </div>
    );
}

function ActiveFilterBadge({
    label,
    onRemove,
    dataTestId,
}: {
    label: string;
    onRemove: () => void;
    dataTestId?: string;
}) {
    return (
        <span data-testid={dataTestId} className="inline-flex max-w-[140px] items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700 sm:max-w-[180px] sm:px-2.5 sm:py-1 sm:text-xs">
            <span className="truncate">{label}</span>
            <button
                onClick={onRemove}
                className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full p-0.5 hover:bg-blue-100"
                aria-label="Kaldır"
                title="Kaldır"
            >
                <X className="h-3 w-3 shrink-0" />
            </button>
        </span>
    );
}
